import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ApiError,
  getAppSettings,
  getLiveStatusForTeam,
  getMatchesByTeamSlug,
  getPublicTeam,
} from "@/lib/api";
import { publicAppSettingsKey, useViewerHostKey } from "@/lib/queryClient";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useMe } from "@/features/auth/useMe";
import { MatchGrid } from "@/features/matches/components/MatchGrid";
import {
  matchHasReadyVideo,
  sortMatchesByDateDesc,
} from "@/features/season/seasonHelpers";
import type { Match } from "@/lib/types";

/**
 * `/teams/$teamSlug` — per-team match list (M12a).
 *
 * Anonymous viewers can open public teams; signed-in members/operators and
 * claimed guests can also open an authorized private team. The backend 404s
 * private teams for unauthorized viewers, caught here as a coherent NotFound
 * surface instead of an opaque error string.
 *
 * The "Watch live" CTA points at the existing global `/live` for now;
 * M12c lifts it to `/teams/$teamSlug/live` and removes the
 * coming-soon note.
 */
export function TeamMatchesPage() {
  const { teamSlug } = useParams({ from: "/teams/$teamSlug" });
  const meQuery = useMe();
  const authProbePending = meQuery.isPending && !meQuery.isFetched;
  const teamQueryKey = useViewerHostKey("public-team", { slug: teamSlug });
  const matchesQueryKey = useViewerHostKey("public-matches", { teamSlug });

  const teamQuery = useQuery({
    queryKey: teamQueryKey,
    queryFn: () => getPublicTeam(teamSlug),
    enabled: !authProbePending,
    staleTime: 60_000,
    retry: (failureCount, err) => {
      // Don't retry the 404 — the team is genuinely non-public / absent.
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const matchesQuery = useQuery({
    queryKey: matchesQueryKey,
    queryFn: () => getMatchesByTeamSlug(teamSlug),
    staleTime: 30_000,
    enabled: !authProbePending && !!teamQuery.data,
    refetchOnWindowFocus: true,
  });

  const [filter, setFilter] = useState<"all" | "home" | "away">("all");
  const [search, setSearch] = useState("");
  const [showPerformance, setShowPerformance] = useState(false);

  const teamMatches = matchesQuery.data ?? [];
  const teamName = teamQuery.data?.name ?? "";
  const teamId = teamQuery.data?.id ?? "";

  // Filter + search applied to the visible-match list. Classification
  // prefers the structured `home_team_id` / `away_team_id` columns
  // (alembic 0004) — added because the legacy string match silently
  // broke on operator typos / casing drift. Rows that predate the
  // migration (or external scrimmages where neither side maps to a
  // tenant) fall back to the case-insensitive string match. Matches
  // where neither classification places this team are not counted
  // toward Home/Away chips.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = teamMatches.filter((m) => {
      if (filter !== "all") {
        const side = sideForTeam(m, teamId, teamName);
        if (filter === "home" && side !== "home") return false;
        if (filter === "away" && side !== "away") return false;
      }
      if (q) {
        const hay = [m.home_team, m.away_team, m.location, m.date]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return sortMatchesByDateDesc(filtered);
  }, [teamMatches, teamId, teamName, filter, search]);

  const performance = useMemo(
    () => computeTeamPerformance(visible, teamId, teamName),
    [visible, teamId, teamName],
  );

  // Pull the club logo from app-settings so every public team page
  // wears the same identity as `/teams`. Reuses the existing
  // `["app-settings"]` cache (also consumed by GameShell / LiveShell
  // / TeamLivePage), so wizard / SettingsPage edits propagate
  // instantly via their shared invalidation. Empty / unset hides the
  // `<img>` entirely — no broken-image placeholder.
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const clubLogoUrl = settingsQuery.data?.assets?.club_logo_url ?? "";

  useDocumentMeta({
    title: teamQuery.data
      ? `${teamQuery.data.name} · tktic`
      : "Team · tktic",
    description: teamQuery.data
      ? `Matches and replays for ${teamQuery.data.name}.`
      : undefined,
    ogType: "website",
  });

  if (authProbePending || teamQuery.isLoading) {
    return (
      <main
        data-testid="team-matches"
        className="min-h-[40vh] bg-canvas text-ink"
      >
        <div className="mx-auto max-w-6xl px-6 py-10">
          <p
            data-testid="team-matches-loading"
            className="font-mono text-sm text-ink-muted"
          >
            Loading team…
          </p>
        </div>
      </main>
    );
  }

  if (teamQuery.isError) {
    const notFound =
      teamQuery.error instanceof ApiError && teamQuery.error.status === 404;
    return (
      <main
        data-testid="team-matches"
        className="min-h-[40vh] bg-canvas text-ink"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-6 py-12">
          <p
            data-testid={notFound ? "team-matches-404" : "team-matches-error"}
            className="font-display text-2xl text-ink"
          >
            {notFound
              ? "Team not found"
              : "Couldn’t load this team. Please refresh in a moment."}
          </p>
          {notFound ? (
            <p className="font-body text-sm text-ink-secondary">
              The team may not have a public page yet. Browse other teams or
              sign in if you&rsquo;re a member.
            </p>
          ) : null}
          <Link
            to="/teams"
            data-testid="team-matches-back"
            className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
          >
            ← back to teams
          </Link>
        </div>
      </main>
    );
  }

  const team = teamQuery.data;
  if (!team) return null;

  return (
    <main
      data-testid="team-matches"
      data-team-slug={team.slug}
      className="min-h-[60vh] bg-canvas text-ink"
    >
      <header className="border-b border-rule px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-4">
          <Link
            to="/teams"
            data-testid="team-matches-breadcrumb"
            className="self-start font-mono text-xs uppercase tracking-wider text-ink-muted hover:text-accent"
          >
            ← Teams
          </Link>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
            <div className="flex items-center gap-4">
              {clubLogoUrl ? (
                <img
                  src={clubLogoUrl}
                  alt=""
                  data-testid="team-matches-club-logo"
                  className="h-12 w-12 shrink-0 rounded object-contain sm:h-16 sm:w-16"
                />
              ) : null}
              <div className="flex flex-col gap-1">
                <h1
                  data-testid="team-matches-name"
                  className="font-display text-3xl tracking-tight text-ink sm:text-4xl"
                >
                  {team.name}
                </h1>
                <p className="font-mono text-xs uppercase tracking-wider text-ink-muted">
                  Match count: <span className="text-ink">{team.match_count}</span>
                  {team.last_match_at ? (
                    <>
                      {" · "}Last: <span className="text-ink">{team.last_match_at}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
            <LiveCta teamSlug={team.slug} />
          </div>
        </div>
      </header>

      <section className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10">
        {/* Filter chips + search + performance toggle */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            role="radiogroup"
            aria-label="Filter matches"
            data-testid="team-matches-filter"
            className="inline-flex shrink-0 rounded border border-rule bg-surface p-0.5 text-xs font-display uppercase tracking-wider"
          >
            {(["all", "home", "away"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={filter === value}
                onClick={() => setFilter(value)}
                data-testid={`team-matches-filter-${value}`}
                data-active={filter === value ? "true" : undefined}
                className="rounded px-3 py-1.5 text-ink-muted transition-colors hover:text-ink data-[active=true]:bg-ink data-[active=true]:text-canvas"
              >
                {value === "all" ? "All matches" : value}
              </button>
            ))}
          </div>
          <div className="flex grow items-center gap-2 sm:max-w-md sm:justify-end">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search matches…"
              data-testid="team-matches-search"
              className="grow rounded border border-rule bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent sm:max-w-xs"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowPerformance((v) => !v)}
            aria-expanded={showPerformance}
            data-testid="team-matches-performance-toggle"
            data-state={showPerformance ? "open" : "closed"}
            className="inline-flex shrink-0 items-center gap-1.5 rounded border border-accent bg-accent px-3 py-1.5 text-xs font-display uppercase tracking-wider text-accent-ink transition-colors hover:bg-accent-hover"
          >
            <span>
              {showPerformance ? "Hide team performance" : "See team performance"}
            </span>
            <span aria-hidden="true">{showPerformance ? "▴" : "▾"}</span>
          </button>
        </div>

        {showPerformance ? (
          <TeamPerformanceSection
            teamName={teamName}
            perf={performance}
            filter={filter}
          />
        ) : null}

        {matchesQuery.isLoading ? (
          <p
            data-testid="team-matches-list-loading"
            className="font-mono text-sm text-ink-muted"
          >
            Loading matches…
          </p>
        ) : matchesQuery.isError ? (
          <p
            data-testid="team-matches-list-error"
            className="font-mono text-sm text-destructive"
          >
            Couldn&rsquo;t load matches.
          </p>
        ) : (
          <MatchGrid
            matches={visible}
            teamSlug={team.slug}
            emptyMessage={
              filter === "all" && !search.trim()
                ? "No matches yet for this team."
                : "No matches match your filter."
            }
            testid="team-matches-grid"
          />
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Team Performance — per-team KPI snapshot.
// ---------------------------------------------------------------------------

type TeamPerformance = {
  matchesPlayed: number;
  goalsFor: number;
  goalsAgainst: number;
  cleanSheets: number;
  replaysAvailable: number;
  teamMatchCount: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalDiff: number;
};

/** Classify which side of a match this team played on. Prefers the
 * structured `home_team_id` / `away_team_id` columns (alembic 0004)
 * over the legacy string match — IDs are robust against operator
 * typos / casing drift; the string match is the fallback for rows
 * created before the migration (or for external scrimmages where
 * neither side maps to a tktic tenant). Returns "none" when neither
 * classification places this team. */
function sideForTeam(
  match: Match,
  teamId: string,
  teamName: string,
): "home" | "away" | "none" {
  if (teamId) {
    if (match.home_team_id && match.home_team_id === teamId) return "home";
    if (match.away_team_id && match.away_team_id === teamId) return "away";
    // Either side has an explicit non-matching id ⇒ trust the data,
    // don't fall through to the string match (the structured signal
    // says this team isn't on either side, e.g. an opponent created
    // the row and tagged THEIR own side).
    if (match.home_team_id || match.away_team_id) {
      // Only short-circuit when at least one side has an explicit id
      // for a different team — that's the cross-tenant case the
      // string fallback would otherwise mis-classify. If BOTH sides
      // are null we still try the string match below.
    }
  }
  const lowerName = teamName.trim().toLowerCase();
  if (!lowerName) return "none";
  // Legacy fallback — applied only when the structured signal didn't
  // place the team explicitly (both columns null, or no team-id
  // available from the caller).
  if (!match.home_team_id && !match.away_team_id) {
    if (match.home_team.trim().toLowerCase() === lowerName) return "home";
    if (match.away_team.trim().toLowerCase() === lowerName) return "away";
  }
  return "none";
}

/** Compute the KPI snapshot for the *visible* match set (filter +
 * search already applied). Mirrors the legacy `renderSeasonTeamStats`
 * math in `js/views.js` of the pre-fork `github.com/humac/replay` repo:
 *   - All KPIs except `replaysAvailable` are gated on having a
 *     recorded score (`score_home` AND `score_away` non-null).
 *   - `replaysAvailable` counts every team-match with a ready video,
 *     scored or not.
 *   - `points = wins*3 + draws`.
 *
 * `teamId` + `teamName` come from the per-team page's resolved
 * `team`. ID classification (alembic 0004) wins; the name fallback
 * preserves legacy behavior for un-migrated rows.
 */
function computeTeamPerformance(
  visible: Match[],
  teamId: string,
  teamName: string,
): TeamPerformance {
  const out: TeamPerformance = {
    matchesPlayed: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    cleanSheets: 0,
    replaysAvailable: 0,
    teamMatchCount: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    points: 0,
    goalDiff: 0,
  };
  if (!teamId && !teamName.trim()) return out;

  for (const m of visible) {
    const side = sideForTeam(m, teamId, teamName);
    if (side === "none") continue;
    const isHome = side === "home";
    out.teamMatchCount += 1;
    if (matchHasReadyVideo(m)) out.replaysAvailable += 1;
    if (m.score_home == null || m.score_away == null) continue;
    out.matchesPlayed += 1;
    const ours = isHome ? m.score_home : m.score_away;
    const theirs = isHome ? m.score_away : m.score_home;
    out.goalsFor += ours;
    out.goalsAgainst += theirs;
    out.goalDiff += ours - theirs;
    if (theirs === 0) out.cleanSheets += 1;
    if (ours > theirs) {
      out.wins += 1;
      out.points += 3;
    } else if (ours === theirs) {
      out.draws += 1;
      out.points += 1;
    } else {
      out.losses += 1;
    }
  }
  return out;
}

function TeamPerformanceSection({
  teamName,
  perf,
  filter,
}: {
  teamName: string;
  perf: TeamPerformance;
  filter: "all" | "home" | "away";
}) {
  const [showRecord, setShowRecord] = useState(false);
  const avgGoals = perf.matchesPlayed
    ? (perf.goalsFor / perf.matchesPlayed).toFixed(1)
    : "0.0";
  const ppg = perf.matchesPlayed
    ? (perf.points / perf.matchesPlayed).toFixed(2)
    : "0.00";

  const cards = [
    {
      label: "Matches played",
      value: String(perf.matchesPlayed),
      note: perf.matchesPlayed === 1
        ? "with a final score recorded"
        : "with final scores recorded",
    },
    {
      label: "Goals scored",
      value: String(perf.goalsFor),
      note: `${avgGoals} per match on average`,
    },
    {
      label: "Clean sheets",
      value: String(perf.cleanSheets),
      note: perf.cleanSheets === 0
        ? "No shutouts yet — there's next week."
        : `Out of ${perf.matchesPlayed} scored matches`,
    },
    {
      label: "Replays available",
      value: String(perf.replaysAvailable),
      note: perf.replaysAvailable === perf.teamMatchCount && perf.teamMatchCount > 0
        ? "Every match has a replay ready to watch."
        : `${Math.max(perf.teamMatchCount - perf.replaysAvailable, 0)} still uploading or processing.`,
    },
  ];

  return (
    <section
      data-testid="team-matches-performance"
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-5 shadow-sm"
    >
      <header className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="font-mono text-[0.6rem] uppercase tracking-wider text-accent">
            Team snapshot
          </p>
          <h2 className="font-display text-xl tracking-tight text-ink sm:text-2xl">
            {teamName ? `${teamName} performance` : "Team performance"}
          </h2>
        </div>
        <p
          data-testid="team-matches-performance-scope-note"
          className="font-body text-xs text-ink-muted"
        >
          {filter === "all"
            ? `All ${teamName || "team"} matches with recorded scores.`
            : filter === "home"
              ? `${teamName || "Team"} home matches only — change the filter for season totals.`
              : `${teamName || "Team"} away matches only — change the filter for season totals.`}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <article
            key={c.label}
            data-testid="team-matches-performance-card"
            className="flex flex-col gap-1 rounded border border-rule bg-surface p-4"
          >
            <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
              {c.label}
            </span>
            <strong className="font-display text-3xl tracking-tight text-ink">
              {c.value}
            </strong>
            <span className="font-body text-xs text-ink-secondary">
              {c.note}
            </span>
          </article>
        ))}
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setShowRecord((v) => !v)}
          aria-expanded={showRecord}
          data-testid="team-matches-performance-record-toggle"
          className="inline-flex items-center gap-1.5 rounded-full border border-rule bg-surface px-4 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted hover:border-accent hover:text-ink"
        >
          <span>{showRecord ? "Hide record" : "Show record"}</span>
          <span aria-hidden="true">{showRecord ? "▴" : "▾"}</span>
        </button>
      </div>

      {showRecord ? (
        <div
          role="group"
          aria-label="Win-loss record"
          data-testid="team-matches-performance-record"
          className="grid grid-cols-1 gap-3 rounded border border-rule bg-elevated p-4 sm:grid-cols-3"
        >
          <div className="flex flex-col gap-0.5 border-l-2 border-accent pl-3">
            <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
              Record
            </span>
            <strong className="font-display text-2xl tracking-tight text-ink">
              {perf.wins}-{perf.draws}-{perf.losses}
            </strong>
            <span className="font-body text-xs text-ink-muted">W-D-L</span>
          </div>
          <div className="flex flex-col gap-0.5 border-l-2 border-good pl-3">
            <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
              Points
            </span>
            <strong className="font-display text-2xl tracking-tight text-ink">
              {perf.points}
            </strong>
            <span className="font-body text-xs text-ink-muted">
              {ppg} per game
            </span>
          </div>
          <div className="flex flex-col gap-0.5 border-l-2 border-flag pl-3">
            <span className="font-mono text-[0.6rem] uppercase tracking-wider text-ink-muted">
              Goal diff
            </span>
            <strong className="font-display text-2xl tracking-tight text-ink">
              {perf.goalDiff > 0 ? `+${perf.goalDiff}` : perf.goalDiff}
            </strong>
            <span className="font-body text-xs text-ink-muted">
              {perf.goalsFor} for · {perf.goalsAgainst} against
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Per-team Live CTA. M12c lit up team-scoped streaming so this now
 * routes at `/teams/$teamSlug/live` instead of the legacy global
 * `/live`.
 *
 * State machine derived from `/api/live/status?team_slug=…` polled
 * every 30 s (matching the season-view CTA cadence):
 *   - `enabled && active && ready` → flashing green `Watch live`
 *     link (live HLS playlist is up; operator wants attention drawn)
 *   - anything else → disabled grey `No live game` pill (route is
 *     still reachable via direct URL — we just don't actively
 *     advertise it)
 *
 * Green uses the brand `--good` token (green-500 light / green-400
 * dark). The "flash" is Tailwind's built-in `animate-pulse`.
 */
function LiveCta({ teamSlug }: { teamSlug: string }) {
  const liveQueryKey = useViewerHostKey("live-status-team", {
    slug: teamSlug,
  });
  const liveQuery = useQuery({
    queryKey: liveQueryKey,
    queryFn: () => getLiveStatusForTeam(teamSlug),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const isLive = Boolean(
    liveQuery.data?.enabled &&
      liveQuery.data?.active &&
      liveQuery.data?.ready,
  );

  if (!isLive) {
    return (
      <span
        data-testid="team-matches-live-cta"
        data-live="false"
        aria-disabled="true"
        className="inline-flex shrink-0 items-center justify-center rounded border border-rule bg-surface px-3 py-1.5 text-sm font-display uppercase tracking-wider text-ink-muted cursor-not-allowed"
      >
        no live game
      </span>
    );
  }

  return (
    <Link
      to="/teams/$teamSlug/live"
      params={{ teamSlug }}
      data-testid="team-matches-live-cta"
      data-live="true"
      className="inline-flex shrink-0 items-center justify-center rounded bg-good px-3 py-1.5 text-sm font-display font-semibold uppercase tracking-wider text-good-ink shadow animate-pulse hover:animate-none hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-good"
    >
      watch live
    </Link>
  );
}
